'use client';
import { useRef, SyntheticEvent, useState } from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'framer-motion';
import { Building2, Stethoscope, ShoppingCart, Car, Briefcase, ChevronDown } from 'lucide-react';

const dashboardSlides = [
  {
    id: 1,
    title: "Visão Geral Executiva",
    description: "KPIs em tempo real e funil de conversão completo.",
    image: "/images/dashboard-visao-geral.png",
    companyName: "Eurovel Multimarcas",
    companyLogo: Car,
    segment: "Concessionária"
  },
  {
    id: 2,
    title: "Gestão de Follow-Up",
    description: "Monitore cada etapa do atendimento e nunca perca uma venda.",
    image: "/images/dashboard-followup.png",
    companyName: "Eurovel Multimarcas",
    companyLogo: Car,
    segment: "Concessionária"
  },
  {
    id: 3,
    title: "Inteligência Artificial",
    description: "Insights preditivos para tomar decisões antes da concorrência.",
    image: "/images/dashboard-ia.png",
    companyName: "Eurovel Multimarcas",
    companyLogo: Car,
    segment: "Concessionária"
  },
  {
    id: 4,
    title: "Hub de Aquisição de Talentos",
    description: "Controle total do pipeline de contratação, com visão unificada de vagas abertas e candidatos no funil.",
    image: "/images/recrutamento-overview.png",
    companyName: "WeCan BR",
    companyLogo: Briefcase,
    segment: "Recursos Humanos"
  },
  {
    id: 5,
    title: "Ranking de Candidatos via IA",
    description: "Nossa IA analisa hard e soft skills para gerar um 'Match Score' preciso, economizando horas de triagem manual.",
    image: "/images/recrutamento-ia-match.png",
    companyName: "WeCan BR",
    companyLogo: Briefcase,
    segment: "Recursos Humanos"
  },
  {
    id: 6,
    title: "Dossiê do Candidato 360°",
    description: "Centralize currículos, notas de entrevista e insights comportamentais em uma única visão detalhada.",
    image: "/images/recrutamento-perfil.png",
    companyName: "WeCan BR",
    companyLogo: Briefcase,
    segment: "Recursos Humanos"
  }
];

function Card({ 
  slide, 
  index, 
  scrollYProgress, 
  total 
}: { 
  slide: typeof dashboardSlides[0], 
  index: number, 
  scrollYProgress: MotionValue<number>, 
  total: number 
}) {
  // Define the time window for this slide
  const step = 1 / total;
  const start = index * step;
  const end = (index + 1) * step;

  // Image Scroll Animation
  // We want the image to start scrolling only after the card is fully visible.
  // So we hold the Y position at 0% for the first 25% of the slide's duration.
  const holdPhase = step * 0.25; 
  
  const imageY = useTransform(
    scrollYProgress, 
    [start, start + holdPhase, end], 
    ["0%", "0%", "-85%"] 
  );

  // Opacity/Visibility Logic
  const opacity = useTransform(
    scrollYProgress,
    [start, start + 0.05, end - 0.05, end],
    [index === 0 ? 1 : 0, 1, 1, 0]
  );
  
  // Scale effect
  const scale = useTransform(
    scrollYProgress,
    [start, start + 0.1, end - 0.1, end],
    [index === 0 ? 1 : 0.95, 1, 1, 0.95]
  );

  return (
    <motion.div
      style={{
        opacity,
        scale,
        zIndex: total - index, // Stack order
        position: 'absolute',
        width: '100%',
        maxWidth: '1000px', // Increased max-width for better fit
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      className="p-4"
    >
      {/* Card Structure */}
      <div className="w-full">
        {/* Image Container (Browser Window) */}
        <div className="relative bg-[#0d121f] rounded-xl overflow-hidden shadow-2xl border border-white/10 w-full aspect-[16/10] group">
            
            {/* Top Bar (Browser UI + Company Info) */}
            <div className="absolute top-0 left-0 w-full h-14 bg-[#1a1f2e]/90 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-4 z-20">
              
              {/* Left: Window Controls + Company Identity */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#ff5f57]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#febc2e]"></div>
                  <div className="w-3 h-3 rounded-full bg-[#28c840]"></div>
                </div>
                
                <div className="w-[1px] h-6 bg-white/10 mx-2"></div>

                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-500/20 rounded-lg">
                    {typeof slide.companyLogo === 'string' ? (
                      <img 
                        src={slide.companyLogo} 
                        alt={`${slide.companyName} logo`} 
                        className="w-4 h-4 object-contain" 
                      />
                    ) : (
                      <slide.companyLogo className="w-4 h-4 text-blue-400" />
                    )}
                  </div>
                  <span className="text-sm font-medium text-white tracking-wide" style={{ fontFamily: 'Raleway' }}>
                    {slide.companyName}
                  </span>
                </div>
              </div>

              {/* Right: Segment Badge */}
              <div className="px-3 py-1 rounded-full bg-white/5 border border-white/10">
                <span className="text-xs text-gray-400 font-medium uppercase tracking-wider" style={{ fontFamily: 'Raleway' }}>
                  {slide.segment}
                </span>
              </div>
            </div>

            {/* Scrollable Image Area */}
            <div className="w-full h-full bg-[#050505] overflow-hidden relative">
                <motion.img 
                    style={{ y: imageY }}
                    src={slide.image} 
                    alt={slide.title}
                    className="w-full absolute top-14 left-0"
                    // Removed object-cover to prevent cropping. 
                    // Width 100% ensures it fits horizontally.
                    onError={(e: SyntheticEvent<HTMLImageElement, Event>) => {
                        e.currentTarget.src = "https://placehold.co/1200x3000/050505/1a3d49?text=" + encodeURIComponent(slide.title);
                    }}
                />
            </div>
        </div>
      </div>
    </motion.div>
  );
}

function MobileCard({ slide }: { slide: typeof dashboardSlides[0] }) {
  const [hasInteracted, setHasInteracted] = useState(false);

  return (
    <div className="w-[85vw] flex-shrink-0 snap-center flex flex-col gap-4">
        {/* Browser Window with Image */}
        <div className="w-full bg-[#0d121f] rounded-xl overflow-hidden shadow-2xl border border-white/10 relative group">
            {/* Top Bar */}
            <div className="absolute top-0 left-0 w-full h-10 bg-[#1a1f2e]/90 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-3 z-20">
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-[#febc2e]"></div>
                        <div className="w-2.5 h-2.5 rounded-full bg-[#28c840]"></div>
                    </div>
                    
                    <div className="w-[1px] h-5 bg-white/10 mx-1"></div>

                    <div className="flex items-center gap-2">
                        <div className="p-1 bg-blue-500/20 rounded-lg">
                        {typeof slide.companyLogo === 'string' ? (
                            <img src={slide.companyLogo} alt={`${slide.companyName} logo`} className="w-3.5 h-3.5 object-contain" />
                        ) : (
                            <slide.companyLogo className="w-3.5 h-3.5 text-blue-400" />
                        )}
                        </div>
                        <span className="text-xs font-medium text-white tracking-wide truncate max-w-[120px]" style={{ fontFamily: 'Raleway' }}>
                        {slide.companyName}
                        </span>
                    </div>
                </div>
            </div>

            {/* Scrollable Image Area */}
            <div 
                className="w-full h-[350px] pt-10 bg-[#050505] relative overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']"
                onTouchStart={() => setHasInteracted(true)}
                onScroll={() => setHasInteracted(true)}
            >
                <img 
                    src={slide.image} 
                    alt={slide.title}
                    className="w-full h-auto object-cover"
                    onError={(e: SyntheticEvent<HTMLImageElement, Event>) => {
                        e.currentTarget.src = "https://placehold.co/1200x3000/050505/1a3d49?text=" + encodeURIComponent(slide.title);
                    }}
                />
            </div>

            {/* Animated Scroll Badge */}
            <div 
                className={`absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-black/70 backdrop-blur-md border border-white/10 rounded-full shadow-lg z-30 transition-opacity duration-500 pointer-events-none ${hasInteracted ? 'opacity-0' : 'opacity-100 animate-bounce'}`}
            >
                <span className="text-xs font-medium text-white whitespace-nowrap">Ver Completo</span>
                <ChevronDown className="w-3 h-3 text-white" />
            </div>
        </div>

        {/* Title & Description (Outside) */}
        <div className="px-1 flex flex-col gap-2">
            <h3 
                className="text-xl font-semibold text-white leading-tight"
                style={{ fontFamily: 'Raleway' }}
            >
                {slide.title}
            </h3>
            <p 
                className="text-sm text-gray-400 leading-relaxed"
                style={{ fontFamily: 'Raleway' }}
            >
                {slide.description}
            </p>
        </div>
    </div>
  );
}

export default function DashboardSection() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end']
  });

  return (
    <section id="dashboard" className="bg-[#02020c] relative">
      
      {/* Section Header - Scrolls normally, appearing before the sticky part */}
      <div className="pt-20 pb-10 px-4 text-center">
          <h2 
              className="text-[30px] sm:text-[45px] lg:text-[60px] font-normal leading-tight text-center mb-4"
              style={{ fontFamily: 'Raleway' }}
          >
              <span className="bg-gradient-to-b from-[#f7faff] to-[#9eb5dc] bg-clip-text text-transparent">O Poder dos </span>
              <span className="text-[#ffffff] font-semibold italic">Dashboards</span>
          </h2>
          <p className="text-gray-400 max-w-lg mx-auto text-base sm:text-lg" style={{ fontFamily: 'Raleway' }}>
              Tenha controle total da sua operação. 
              <span className="hidden lg:inline"> Role para explorar cada detalhe.</span>
              <span className="lg:hidden block mt-1 text-sm text-blue-400/80">Deslize para ver os modelos &rarr;</span>
          </p>
      </div>

      {/* Mobile View (Horizontal Slider) */}
      <div className="lg:hidden flex overflow-x-auto snap-x snap-mandatory w-full gap-4 px-4 pb-20 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
        {dashboardSlides.map((slide) => (
          <MobileCard key={slide.id} slide={slide} />
        ))}
      </div>

      {/* Desktop Sticky View */}
      <div ref={containerRef} className="hidden lg:block h-[700vh] relative">
        <div className="sticky top-0 h-screen overflow-hidden flex flex-col items-center justify-center py-12">
            
            {/* Stacked Cards Container */}
            <div className="relative w-full max-w-[1200px] flex-1 flex items-center justify-center min-h-0 px-6">
            {dashboardSlides.map((slide, index) => (
                <Card 
                    key={slide.id} 
                    slide={slide} 
                    index={index} 
                    scrollYProgress={scrollYProgress} 
                    total={dashboardSlides.length} 
                />
            ))}
            </div>
            
            {/* Scroll Indicator */}
            <motion.div 
                style={{ opacity: useTransform(scrollYProgress, [0.95, 1], [1, 0]) }}
                className="flex flex-col items-center gap-2 text-white/30 text-xs uppercase tracking-widest z-30 shrink-0 mt-8"
            >
                <span>Scroll para ler</span>
                <div className="w-[1px] h-8 bg-gradient-to-b from-white/30 to-transparent"></div>
            </motion.div>

        </div>
      </div>
    </section>
  );
}
